import { useQuery } from '@apollo/client';
import { ReactComponent as SearchIcon } from 'assets/svg/search.svg';
import CustomLayout from 'component/layout/custom_layout';
import LoadingDataSpinner from 'component/loading_spinner/loading_data_spinner';
import { StandardAlert } from 'component/StandardAlert';
import StandardPopup from 'component/StandardPopup';
import { cache } from 'graph_ql';
import { USER_DETAIL } from 'graph_ql/authentication/queries';
import { SEARCH_STYLIST } from 'graph_ql/search/stylist_search';
import { useQueryData } from 'hooks/useQuery';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import queryString from 'query-string';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getUserProfilePhoto } from 'utils/getUserProfilePhoto';
import UserType from 'constants/user';
import {
    changeTriggerUpdateSearch,
    ESortType,
    setExpertiseFilter,
    setLevelFilter,
    setSearchTerm,
    setSearchTermChanged,
    setShowFilter,
} from 'redux/filter/filterSlice';
import { RootState, useAppSelector } from 'redux/store';
import { PATH } from 'routes/constant';
import noStyleIcon from '../../assets/svg/no_style_v2.svg';
import config from '../../config';
import { PRODUCT_TYPE, trackEvent, TRACK_EVENT } from '../../utils/analytics';
import { useStyles } from './style';
import InfiniteScroll from 'react-infinite-scroller';
import { AlgoliaInput, AlgoliaResponse } from 'graph_ql/types/types';
import { IAlgoliaUser } from 'graph_ql/users/types';
import utils from 'utils/utils';
import { buildStoreFilter } from '../../utils/getQuerySearchStyles';

const StyledSearchIcon = () => <SearchIcon style={{ width: 64, height: 64, marginTop: 45 }} />;

interface IQueryParamProps {
    search?: string;
    bodyShape?: string;
    bodyType?: string;
    category?: string;
    gender?: string;
    priceMax?: string;
    priceMin?: string;
    sortBy?: ESortType;
}

enum Efilters {
    EXPERTISE = 'expertise',
    LEVEL = 'level',
}
export interface IFeedProps {
    isSearch?: boolean;
    searchKey?: string;
    title?: string;
}
const StylistFeed: React.FC<IFeedProps> = ({ searchKey = '' }) => {
    const [modalShow, setModalShow] = useState({
        show: false,
        title: '',
        content: '',
        button1: '',
        button2: '',
        path: '',
        type: '',
        closeButton: true,
        onFirstButtonClick: () => {},
        onSecondButtonClick: () => {},
        Icon: StyledSearchIcon,
    });
    const [searchInput, setSearchInput] = useState<String>(searchKey);
    const user: any = useQueryData(USER_DETAIL, null, true, true);
    const classes = useStyles();
    const dispatch = useDispatch();
    const [heightContent, setHeightContent] = useState<any>();
    const me = useSelector((state: RootState) => state.userDetail.me);
    const userDetail = useMemo(() => user?.dataQuery?.me, [user]);
    const currentUserId = userDetail?.id || me?.id;
    const {
        expertiseFilterList = [],
        levelFilterList = [],
        triggerUpdateSearch,
        searchTerm,
    } = useAppSelector((state: RootState) => state.filter);
    const history = useHistory();
    const [listStylist, setListSylist] = useState<any>([]);
    const [expertiseValues, setExpertiseValues] = useState<any>([]);
    const [levelValues, setLevelValues] = useState<any>([]);
    const location: any = useLocation();
    let queryParams = queryString.parse(location.search);
    const { search, ...restQueryParams }: IQueryParamProps = queryParams;
    const store = useSelector((state: RootState) => state.widget.store);
    const [currentPage, setCurrentPage] = useState<number>(0);

    useEffect(() => {
        const expertiseQueryParamsList = createFilterList({ key: Efilters.EXPERTISE });
        let expertiseOptions = expertiseValues.map((item) => ({
            title: item,
            value: item,
            isActive: !!expertiseQueryParamsList.find((i) => i.value === item),
            isShow: true,
        }));
        dispatch(setExpertiseFilter(expertiseOptions));
        // eslint-disable-next-line
    }, [expertiseValues]);

    useEffect(() => {
        const levelQueryParamsList = createFilterList({ key: Efilters.LEVEL });
        let levelOptions = levelValues.map((item) => ({
            title: item,
            value: item,
            isActive: !!levelQueryParamsList.find((i) => i.value === item),
            isShow: true,
        }));
        dispatch(setLevelFilter(levelOptions));
        // eslint-disable-next-line
    }, [levelValues]);

    //this create filter list function creates the filter json based on the query params.
    const createFilterList = ({ key, initialList = [] }: any) => {
        let queryParams = queryString.parse(location.search);
        const { search, ...rest }: IQueryParamProps = queryParams;

        if (Object.keys(rest)?.includes(key)) {
            let filterOptions = rest[key].split(',');
            const tempFilterList = filterOptions.map((item) => {
                return {
                    title: item,
                    value: item,
                    isActive: rest[key] ? rest[key].includes(item) : false,
                    isShow: true,
                };
            });
            return tempFilterList;
        } else {
            return initialList;
        }
    };

    // this useeffect is to manipulate the query params based on the applied filter. For future filters, need to dependency and include it in filterlist array.
    useEffect(() => {
        let existingParams = queryString.parse(location.search);
        let params = { ...existingParams };
        const filterLists = [
            { key: Efilters.EXPERTISE, list: expertiseFilterList, options: expertiseValues },
            { key: Efilters.LEVEL, list: levelFilterList, options: levelValues },
        ];
        filterLists.forEach((filter) => {
            if (filter.list.length) {
                let selectedFilterOption: string[] = [];
                filter.list.forEach((item) => {
                    if (item.isActive) {
                        selectedFilterOption = [...selectedFilterOption, item.value];
                    }
                });
                if (selectedFilterOption.length && filter.options.length) {
                    //filter.option is to check whether the query params not based on the persist redux
                    params = { ...params, [filter.key]: selectedFilterOption.toString() };
                } else if (location.search.includes(filter.key) && filter.options.length) {
                    delete params[filter.key];
                }
            } else if (filter.options.length && triggerUpdateSearch) {
                // to delete the query params while clicking clear filter button
                delete params[filter.key];
            }
        });
        history.replace({ ...location, pathname: location.pathname, search: queryString.stringify(params) });

        // eslint-disable-next-line
    }, [expertiseValues, levelValues, location.pathname, location.search, triggerUpdateSearch]);

    const searchVariables = useMemo(() => {
        cache.modify({
            fields: {
                locationsInRadius(existingTaskRefs, { readField }) {
                    return {};
                },
            },
        });
        let filters = `userType:${UserType.Stylist} AND approved:true`;
        if (utils.isAppInWidgetMode()) {
            filters += ` AND (${buildStoreFilter(store)})`;
        } else {
            filters += ` AND featured: true`;
        }
        const activeLevelFilters = levelFilterList.filter((x) => x.isActive);
        if (activeLevelFilters.length) {
            filters += ` AND (${activeLevelFilters.map((x) => `level.title:"${x.title}"`).join(' OR ')})`;
        }

        const activeExpertiseFilters = expertiseFilterList.filter((x) => x.isActive);
        if (activeExpertiseFilters.length) {
            filters += ` AND (${activeExpertiseFilters
                .map((x) => `stylistExpertise.value:"${x.title}"`)
                .join(' OR ')})`;
        }
        return {
            index: config.algolia.algolia_prefix + 'users',
            query: searchKey || '',
            filters,
            hit: config.algoliaHitPerPage,
            page: 0,
            facets: ['level.title', 'stylistExpertise.value'],
        };
        // eslint-disable-next-line
    }, [expertiseValues, levelValues, searchKey, triggerUpdateSearch, location.search]);

    const onGetDataStyleComplete = (response) => {
        const { hits, nbHits, page, facets } = response.locationsInRadius;
        setListSylist(hits);
        if (searchKey !== searchTerm) {
            dispatch(setSearchTerm(searchKey));
            dispatch(setSearchTermChanged(true));
        }
        if (page === 0) {
            dispatch(setShowFilter(true));
            if (searchKey !== searchTerm || !isEmpty(restQueryParams)) {
                // dispatch(resetFilter())
                dispatch(setSearchTerm(searchKey));
                dispatch(setSearchTermChanged(true));
            } else {
                dispatch(setSearchTermChanged(false));
            }
            if (hits.length) {
                setLevelValues(Object.keys(facets.find((facet: any) => facet.name === 'level.title')?.data || {}));
                setExpertiseValues(
                    Object.keys(facets.find((facet: any) => facet.name === 'stylistExpertise.value')?.data || {}),
                );
            }

            dispatch(changeTriggerUpdateSearch(false));

            trackEvent(TRACK_EVENT.STYLIST_SEARCH, {
                userId: currentUserId,
                userType: userDetail?.userType || me?.userType,
                productType: PRODUCT_TYPE.STYLE,
                numberOfStylist: nbHits,
                query: searchKey,
                filter: {
                    level: levelFilterList.filter((x) => x.isActive).map((x) => x.title),
                    expertise: expertiseFilterList.filter((x) => x.isActive).map((x) => x.title),
                },
            });
        }
    };

    const {
        loading,
        fetchMore,
        data: stylistData,
    } = useQuery<AlgoliaResponse<IAlgoliaUser, 'locationsInRadius'>, AlgoliaInput>(SEARCH_STYLIST, {
        variables: searchVariables,
        onCompleted(response) {
            onGetDataStyleComplete(response);
        },
        skip: isNull(searchVariables),
        fetchPolicy: 'cache-first',
    });

    // const itemsRefs: any = useRef([]);
    // const handleFocusItemOnHomePageNavigated = (selectedId) => {
    //     const index = stylistData?.locationsInRadius.hits.findIndex((item) => item.id === selectedId);
    //     if (index && index >= 0 && itemsRefs.current.length) {
    //         itemsRefs?.current[index]?.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
    //     }
    // };
    const stylistActiveId = location?.state?.stylistId;
    const focusHandler = useCallback((node, isFocus) => {
        if (!node) return;
        if (isFocus && !heightContent) {
            node.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
        }
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        if (triggerUpdateSearch || searchInput) {
            setHeightContent('100%');
        }

        // eslint-disable-next-line
    }, [triggerUpdateSearch, searchInput]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if ((e.target && e.target.value !== searchKey) || (e.suggestion && e.suggestion !== searchKey)) {
                dispatch(setShowFilter(true));
                dispatch(setSearchTerm(searchKey));
            }

            history.push({
                pathname: PATH.STYLISTS,
                search: `?search=${e.suggestion || searchInput || ''}`,
            });
        }
    };

    const fetchMoreStylists = () => {
        if (!stylistData) {
            return;
        }

        const { nbPages } = stylistData.locationsInRadius;

        if (currentPage > nbPages - 1) {
            return;
        }

        fetchMore({
            variables: {
                page: currentPage + 1,
            },
            updateQuery: (prev: any, { fetchMoreResult }): any => {
                let result;
                setCurrentPage(fetchMoreResult?.locationsInRadius?.page);
                if (!fetchMoreResult || fetchMoreResult.locationsInRadius.page <= prev.locationsInRadius.page) {
                    result = prev;
                } else {
                    const newPrev = isEmpty(prev.locationsInRadius) ? stylistData : prev;
                    result = {
                        ...newPrev,
                        locationsInRadius: {
                            ...newPrev.locationsInRadius,
                            hits: [...(listStylist || []), ...(fetchMoreResult.locationsInRadius.hits || [])]
                                .filter(Boolean)
                                .flat(),
                            page: fetchMoreResult.locationsInRadius.page,
                            nbPages: fetchMoreResult.locationsInRadius.nbPages,
                            nbHits: fetchMoreResult.locationsInRadius.nbHits,
                            facets: fetchMoreResult.locationsInRadius.facets,
                        },
                    };
                }
                onGetDataStyleComplete(result);
            },
        });
    };

    return (
        <>
            {loading && <LoadingDataSpinner isBackground={true} />}
            <CustomLayout
                title="Stylists"
                showHeaderFilter={true}
                handler={handleKeyDown}
                setInputSearch={setSearchInput}
                value={searchInput}
                style={{ backgroundColor: '#f2f2f2' }}
                containerStyle={{ overflow: 'auto' }}
            >
                <div className={classes.main} id="main" style={{ height: heightContent }}>
                    {listStylist && listStylist.length
                        ? !loading && (
                            <InfiniteScroll
                                pageStart={1}
                                loadMore={() => {
                                    fetchMoreStylists();
                                }}
                                hasMore={stylistData && currentPage < stylistData?.locationsInRadius?.nbPages - 1}
                                className={`px-3 ${classes.stylistList}`}
                                useWindow={false}
                            >
                                {listStylist.map((item: any, i) => (
                                    <div
                                        className={classes.singleStylist}
                                        key={i}
                                        ref={(node) => {
                                            const focusIndex = stylistData?.locationsInRadius.hits.findIndex(
                                                (item) => item.id === stylistActiveId,
                                            );
                                            focusHandler(node, focusIndex === i);
                                        }}
                                    >
                                        <div className={classes.profileImageBox}>
                                            <img
                                                className={classes.profileImage}
                                                src={getUserProfilePhoto(item)}
                                                alt={item.userName}
                                            />
                                            {item?.level?.imageUrl ? (
                                                <img
                                                    className={classes.stylistLevel}
                                                    src={item?.level?.imageUrl}
                                                    alt={item?.level?.title}
                                                />
                                            ) : null}
                                        </div>
                                        <div className={classes.detailBox}>
                                            <div className={classes.stylistName}>
                                                {item.firstName + ' ' + item.lastName}
                                            </div>
                                            <div className={classes.stylistBio}>{item.bio}</div>
                                        </div>
                                        <button
                                            className={classes.selectBtn}
                                            onClick={() => history.push(`${PATH.STYLIST_PROFILE}/${item?.userName}`)}
                                        >
                                              View
                                        </button>
                                    </div>
                                ))}
                            </InfiniteScroll>
                        )
                        : !loading && (
                            <div style={{ height: '100%', padding: '30px 24px 0' }}>
                                <StandardAlert
                                    height={`calc( ${heightContent}px - 100px)`}
                                    icon={noStyleIcon}
                                    title="No Matching Stylists"
                                    body="Sorry we don't have any stylist matching your requirements"
                                    isIcon={true}
                                />
                            </div>
                        )}
                </div>
            </CustomLayout>
            {modalShow.show && (
                <StandardPopup
                    show={modalShow.show}
                    onHide={() => {
                        setModalShow({ ...modalShow, show: false });
                    }}
                    title={modalShow.title}
                    content={modalShow.content}
                    button1={modalShow.button1}
                    button2={modalShow.button2}
                    type={modalShow.type as any}
                    closeButton={modalShow.closeButton}
                    onFirstButtonClick={modalShow.onFirstButtonClick}
                    onSecondButtonClick={modalShow.onSecondButtonClick}
                    Icon={modalShow.Icon}
                />
            )}
        </>
    );
};
StylistFeed.whyDidYouRender = true;
export default StylistFeed;
